import axios from "axios";
import { SSO_PROVIDER } from "@src/utils/constants";
import { getSsoSettings } from "@src/views/auth/SingleSignOnView/ssoProviderHelper";

/**
 * Direct-bearer session for Phase Two (OI-25014, mirroring mobile's OI-24930).
 *
 * With the API's `PhaseTwoAuth` flag on, the Keycloak access token IS the API
 * credential: no `external-login` code exchange, refresh goes to the realm
 * token endpoint, and sign-out ends the Keycloak session. The tokens live in
 * sessionStorage next to the existing `user` entry so both halves of the
 * session share a lifetime; the AxiosSetup interceptors consult this module on
 * every request/refresh.
 */
export interface PhaseTwoDirectSession {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  /** Epoch seconds, informational — refresh is driven by 401 retry, not expiry. */
  expiresAt: number | null;
}

/** The API feature flag this mode is gated on (`AppConstants.FeatureFlags`). */
export const PHASE_TWO_AUTH_FLAG_KEY = "PhaseTwoAuth";

const STORAGE_KEY = "phaseTwoDirectSession";

export const storeDirectSession = (session: PhaseTwoDirectSession): void => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
};

export const getDirectSession = (): PhaseTwoDirectSession | null => {
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as PhaseTwoDirectSession;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

export const hasDirectSession = (): boolean => getDirectSession() !== null;

export const clearDirectSession = (): void => {
  sessionStorage.removeItem(STORAGE_KEY);
};

const getPhaseTwoAuthority = (): string | null => {
  const ssoSettings = getSsoSettings(SSO_PROVIDER.PhaseTwo);
  return ssoSettings?.authority ?? null;
};

const getPhaseTwoClientId = (): string | null => {
  const ssoSettings = getSsoSettings(SSO_PROVIDER.PhaseTwo);
  return ssoSettings?.client_id ?? null;
};

interface RealmTokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
}

// Bare axios instance with no app interceptors. The realm token endpoint is
// cross-origin: it must not carry OpenUp request headers / the API bearer, and
// a failure here must not recurse into AxiosSetup's 401-retry interceptor.
// `axios.create()` starts with empty interceptor stacks — the app registers
// interceptors only on the default `axios` instance and the two OpenUp clients.
const realmHttp = axios.create();

let refreshPromise: Promise<PhaseTwoDirectSession> | null = null;

const requestRefreshedSession = async (): Promise<PhaseTwoDirectSession> => {
  const current = getDirectSession();
  const authority = getPhaseTwoAuthority();
  const clientId = getPhaseTwoClientId();
  if (!current?.refreshToken || !authority || !clientId) {
    clearDirectSession();
    throw new Error("Phase Two direct session cannot refresh");
  }

  try {
    // Uses `realmHttp` (see above), not the intercepted default instance, so
    // this cross-origin call carries no OpenUp headers / bearer and cannot
    // recurse into the 401-retry interceptor. The realm URL is absolute, so
    // baseURL is irrelevant.
    const { data } = await realmHttp.post<RealmTokenResponse>(
      `${authority}/protocol/openid-connect/token`,
      new URLSearchParams({
        grant_type: "refresh_token",
        client_id: clientId,
        refresh_token: current.refreshToken,
      }),
      { withCredentials: false },
    );

    const refreshed: PhaseTwoDirectSession = {
      accessToken: data.access_token,
      refreshToken: data.refresh_token || current.refreshToken,
      idToken: data.id_token || current.idToken,
      expiresAt: data.expires_in
        ? Math.floor(Date.now() / 1000) + data.expires_in
        : null,
    };
    storeDirectSession(refreshed);
    return refreshed;
  } catch (error) {
    // A rejected refresh (revoked session, expired refresh token) ends the
    // direct session — the caller's failure path signs the user out locally.
    clearDirectSession();
    throw error;
  }
};

/** Single-flight refresh against the realm token endpoint. */
export const refreshDirectSession = (): Promise<PhaseTwoDirectSession> => {
  refreshPromise ??= requestRefreshedSession().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
};

/**
 * Keycloak end-session URL for sign-out (kills the realm SSO cookie, then
 * returns to the app). Null when the environment has no Phase Two config.
 */
export const getPhaseTwoEndSessionUrl = (idToken: string): string | null => {
  const authority = getPhaseTwoAuthority();
  if (!authority) return null;
  const params = new URLSearchParams({
    post_logout_redirect_uri: window.location.origin,
  });
  if (idToken) {
    params.set("id_token_hint", idToken);
  }
  return `${authority}/protocol/openid-connect/logout?${params.toString()}`;
};
